import { FC, MouseEventHandler, useMemo } from 'react';

import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { ROUTE_MY_ACCOUNT_PAGE } from 'src/routes';
import { useTHelper } from 'src/utils/i18n';
import { PaymentMethodType, PaymentMethodTypeEnum, PaymentTypeIdEnum } from 'src/models/paymentMethod';
import { API_URL } from 'src/config';
import { PaymentMethodVisaIcon } from 'src/components/common/PaymentMethod/icons/Visa';
import { SafeAnyType } from 'src/utils/safeAny';
import { PaymentMethodGooglePayIcon } from 'src/components/common/PaymentMethod/icons/GooglePay';
import { PaymentMethodApplePayIcon } from 'src/components/common/PaymentMethod/icons/ApplePay';
import { PaymentMethodPaypalIcon } from 'src/components/common/PaymentMethod/icons/Paypal';
import { PaymentMethodCheckIcon } from 'src/components/common/PaymentMethod/icons/Check';
import { PaymentMethodRazorPayIcon } from 'src/components/common/PaymentMethod/icons/RazorPay';
import { PaymentMethodStripeLinkIcon } from 'src/components/common/PaymentMethod/icons/StripeLink';

import styles from 'src/styles/common/paymentMethod.module.scss';
import { ChildrenPropType } from 'src/utils/types';
import classNames from 'classnames';

type Props = {
  model: Pick<
    PaymentMethodType,
    | 'PaymentTypeID'
    | 'CreditCardPaymentMethodTypeID'
    | 'CardBrand'
    | 'CardLast4'
    | 'CardExpDate'
    | 'AdditionalInfo'
    | 'StripeLinkEmail'
  >;

  showManage?: boolean;
  children?: ChildrenPropType;
  onClick?: MouseEventHandler<HTMLDivElement>;
  className?: string;
  noOnlyBrandState?: boolean;
};

export type PaymentMethodProps = Props;

// https://stripe.com/docs/testing?testing-method=card-numbers
export const PaymentMethod: FC<Props> = ({ model, showManage, children, onClick, className, noOnlyBrandState }) => {
  const tHelper = useTHelper('common');

  const brand = useMemo(() => {
    let className;
    let Logo: SafeAnyType;

    switch (model.PaymentTypeID) {
      case PaymentTypeIdEnum.stripeCreditCard:
      case PaymentTypeIdEnum.payPalCreditCard:
      case PaymentTypeIdEnum.stripeLink: {
        if (model.CreditCardPaymentMethodTypeID === PaymentMethodTypeEnum.creditCard) {
          if (model.CardBrand === 'visa') {
            className = styles.visa;
            Logo = PaymentMethodVisaIcon;
            break;
          }

          const basePath = `${process.env.NODE_ENV === 'development' ? API_URL : ''}/Content/images/card-brands/`;
          let src = '';

          switch (model.CardBrand) {
            case 'amex':
              src = `${basePath}amex.svg`;
              break;
            case 'diners':
              src = `${basePath}diners.svg`;
              break;
            case 'discover':
              src = `${basePath}discover.svg`;
              break;
            case 'jcb':
              src = `${basePath}jcb.svg`;
              break;
            case 'mastercard':
              src = `${basePath}mastercard.svg`;
              break;
            case 'unionpay':
              src = `${basePath}unionpay.svg`;
              break;
            default:
              src = `${basePath}unknown.svg`;
              break;
          }

          return <img key="brand" className={styles.brand} src={src} alt={model.CardBrand} data-testid="imgBrand" />;
        }

        if (
          model.CreditCardPaymentMethodTypeID === PaymentMethodTypeEnum.applePay ||
          model.CreditCardPaymentMethodTypeID === PaymentMethodTypeEnum.googlePay
        ) {
          className = styles.stripe;
          Logo =
            model.CreditCardPaymentMethodTypeID === PaymentMethodTypeEnum.googlePay
              ? PaymentMethodGooglePayIcon
              : PaymentMethodApplePayIcon;
        } else if (model.CreditCardPaymentMethodTypeID === PaymentMethodTypeEnum.link) {
          className = styles.stripeLink;
          Logo = PaymentMethodStripeLinkIcon;
        }

        break;
      }

      case PaymentTypeIdEnum.payPal: {
        className = styles.paypal;
        Logo = PaymentMethodPaypalIcon;
        break;
      }

      case PaymentTypeIdEnum.check: {
        className = styles.check;
        Logo = PaymentMethodCheckIcon;
        break;
      }

      case PaymentTypeIdEnum.razorPay: {
        className = styles.razorPay;
        Logo = PaymentMethodRazorPayIcon;
        break;
      }

      default: {
        const basePath = `${process.env.NODE_ENV === 'development' ? API_URL : ''}/Content/images/card-brands/`;
        const src = `${basePath}unknown.svg`;
        return <img key="brand" className={styles.brand} src={src} alt="paymnentMethod" data-testid="imgBrand" />;
      }
    }

    return <Logo key="brand" className={`${styles.brand} ${className}`} />;
  }, [model.CardBrand, model.PaymentTypeID, model.CreditCardPaymentMethodTypeID]);

  const showOnlyBrand =
    !noOnlyBrandState &&
    model.PaymentTypeID &&
    ([PaymentTypeIdEnum.payPal, PaymentTypeIdEnum.check, PaymentTypeIdEnum.razorPay].includes(model.PaymentTypeID) ||
      (model.PaymentTypeID === PaymentTypeIdEnum.stripeLink &&
        model.CreditCardPaymentMethodTypeID === PaymentMethodTypeEnum.link));

  const showLinkData =
    model.CreditCardPaymentMethodTypeID && model.CreditCardPaymentMethodTypeID === PaymentMethodTypeEnum.link;

  return (
    <div role="presentation" className={classNames(styles.paymentMethod, className)} onClick={onClick}>
      {showOnlyBrand && !model.AdditionalInfo && (
        <>
          {showLinkData ? (
            <div className={styles.infoContainer}>
              <div className={styles.brandContainer}>{brand}</div>
              {showLinkData && <div className={styles.linkEmail}>{model.StripeLinkEmail}</div>}
            </div>
          ) : (
            brand
          )}
        </>
      )}

      {showOnlyBrand && model.AdditionalInfo && (
        <div className={`${styles.info} ${styles.additionalInfo}`}>
          {brand}
          <span>{model.AdditionalInfo}</span>
          {children}
        </div>
      )}

      {!showOnlyBrand && (
        <div className={styles.info}>
          {brand}
          <div className={styles.cardData}>
            <div className={styles.last}>
              <div className={styles.dots}>
                <div className={styles.dot} />
                <div className={styles.dot} />
                <div className={styles.dot} />
                <div className={styles.dot} />
              </div>
              <span>{model.CardLast4}</span>
            </div>
            {children || <div className={styles.exp}>{model.CardExpDate}</div>}
          </div>
        </div>
      )}

      {showManage && (
        <UiInnerNotSPALink
          className={styles.button}
          href={`${ROUTE_MY_ACCOUNT_PAGE}?paymentmethod`}
          dataTestId="manage"
        >
          {tHelper('managePaymentMethod')}
        </UiInnerNotSPALink>
      )}
    </div>
  );
};
